import axios, { AxiosResponse } from "axios"

import {
  AddWebhookResponse,
  APIKeyResponse,
  EventRuleAvailabilityPayload,
  EventRuleAvailabilityResponse,
  EventRuleDimensionOptionsResponse,
  EventRulesResponse,
  EventRuleTopic,
  GetWebhooksResponse,
  IntegrationNotificationsApiResponse,
  IntegrationNotificationsResponse,
  SaveEventRulePayload,
  SaveEventRuleResponse,
  SwitchEventRulePayload,
  UpdateIntegrationNotificationsPayload,
} from "@/api/integration/type"
import baseRequest from "@/utils/baseRequest"
import request from "@/utils/request"

export default {
  /**
   * ETA 集成
   *
   * @param connect 传入 true 表示启用连接。传入 false 表示断开连接
   */
  ETAConnect(connect = true) {
    return request.post("integration/eta", { connect })
  },

  /**
   * 添加 Webhooks 配置
   */
  addWebhook(param: {
    url: string
    trigger: number
    action: number
  }) {
    return baseRequest.post<AddWebhookResponse>("v4/integration/webhooks", param, {
      // @ts-ignore
      useDefaultExceptionHandler: false,
    }) as Promise<AxiosResponse<AddWebhookResponse>>
  },

  /**
   * klaviyo 绑定
   */
  bindKlaviyo(param: {
    api_key: string
  }) {
    return request.post("/integration/klaviyo/bind", param)
  },

  connectGorgias(param: {
    base_api: string,
    api_key: string,
    username: string
  }) {
    return request.post("/integration/gorgias", param)
  },

  /**
   * 删除 Webhooks 配置
   */
  delWebhook(param: {
    id: number
  }) {
    return baseRequest.delete<void>(`v4/integration/webhooks/${param.id}`)
  },

  /**
   * 测试 Webhooks 配置
   */
  testWebhook(param: {
    id: number
  }) {
    return baseRequest.post<void>(`/v4/integration/webhooks/${param.id}/send_test`)
  },

  /**
   * 获取 APP 信息
   */
  getAppDetail() {
    return request.get("integration/app-detail?version=2")
  },

  /**
   * 获取 APP 列表
   */
  getAppList() {
    return baseRequest.get("v2/integration/apps")
  },

  getConnectData() {
    return baseRequest.get("v2/integration/apps/config")
  },

  /**
   * Omnisend 绑定
   */
  bindOmniSend(param: {
    api_key: string
  }) {
    return request.post("/integration/omnisend/bind", param)
  },

  /**
   * klaviyo 取消绑定
   */
  getAppUnbind() {
    return request.post("/integration/klaviyo/unbind")
  },

  /**
   * Paypal 取消绑定
   */
  unbindPaypal() {
    return baseRequest.post("v2/integration/paypal/unbind")
  },

  disconnectGorgias(id: number) {
    return request.delete(`/integration/gorgias/${id}`)
  },

  /**
   * 获取推荐的 APP 列表
   */
  getRecommendedApps() {
    return request.get("integration/recommended-apps/get")
  },

  /**
   * Omnisend 取消绑定
   */
  getAppOmniUnbind() {
    return request.post("/integration/omnisend/unbind")
  },

  getGorgias() {
    return request.get("/integration/gorgias")
  },

  /**
   * 获取 Webhooks 配置信息
   */
  getWebhooks() {
    return baseRequest.get<GetWebhooksResponse>("v4/integration/webhooks", {
      // @ts-ignore
      useDefaultExceptionHandler: false,
    }) as Promise<AxiosResponse<GetWebhooksResponse>>
  },

  /**
   * 获取 API Key 信息
   */
  getAPIKey() {
    return baseRequest.get<APIKeyResponse>("v4/integration/api_key")
  },

  /**
   * 检测 Intercom 是否绑定
   */
  getIntercomAppData() {
    return request.get("/integration/intercom/check")
  },

  /**
   *  检测 Omnisend 是否绑定
   */
  getOmniSendAppData() {
    return request.get("/integration/omnisend/get-data")
  },

  getPaypalData() {
    return request.get("/integration/paypal/connect?way=paypal-connect-check")
  },

  getPaypalUnbind() {
    return request.get("/integration/paypal/connect?way=paypal-unbind")
  },

  getPaypalUrl() {
    // return request.get("/integration/paypal/connect?way=connect-paypal-link")
    return baseRequest.get("v2/integration/paypal/login-url")
  },

  connectYotpo() {
    return baseRequest.get("v2/integration/yotpo/connect")
  },

  disconnectYotpo() {
    return baseRequest.delete("v2/integration/yotpo/disconnect")
  },

  connectKlaviyo() {
    return baseRequest.get("v2/integration/klaviyo/connect")
  },

  disconnectKlaviyo() {
    return baseRequest.delete("v2/integration/klaviyo/disconnect")
  },

  /**
   *  检测 klaviyo 是否绑定
   */
  getklaviyoAppData() {
    return request.get("/integration/klaviyo/get-data")
  },

  /**
   * 获取集成通知配置
   */
  getIntegrationNotifications() {
    return baseRequest.get<IntegrationNotificationsResponse>("v2/integration/notifications")
  },

  /**
   * 更新集成通知配置（支持只传改动的事件字段，局部更新）
   */
  updateIntegrationNotifications(data: UpdateIntegrationNotificationsPayload) {
    return baseRequest.put<IntegrationNotificationsResponse>("v2/integration/notifications", data)
  },

  /**
   * 获取事件规则列表
   */
  getEventRules() {
    return baseRequest.get<EventRulesResponse>("v2/integration/event-rules")
  },

  /**
   * 新建事件规则
   */
  saveEventRule(data: SaveEventRulePayload) {
    return baseRequest.post<SaveEventRuleResponse>("v2/integration/event-rules", data)
  },

  /**
   * 更新事件规则（修改维度 / 天数）
   */
  updateEventRule(ruleId: string, data: SaveEventRulePayload) {
    return baseRequest.put<SaveEventRuleResponse>(`v2/integration/event-rules/${ruleId}`, data)
  },

  /**
   * 启用 / 停用事件规则（成功 / 失败提示由 store 统一 toast，关闭默认异常提示避免重复）
   */
  switchEventRule(ruleId: string, data: SwitchEventRulePayload) {
    return baseRequest.patch<SaveEventRuleResponse>(`v2/integration/event-rules/${ruleId}/switch`, data, {
      // @ts-ignore
      useDefaultExceptionHandler: false,
    })
  },

  /**
   * 删除事件规则
   */
  deleteEventRule(ruleId: string) {
    return baseRequest.delete<void>(`v2/integration/event-rules/${ruleId}`)
  },

  /**
   * 获取事件规则的维度可选项（承运商 / 状态 / 地点 / 目的地）
   * @param topic 规则主题：shipment_transit_delay | shipment_stalled
   */
  getEventRuleDimensionOptions(topic: EventRuleTopic) {
    return baseRequest.get<EventRuleDimensionOptionsResponse>("v2/integration/event-rules/dimension-options", {
      params: { topic },
    })
  },

  /**
   * 选项可用性检测：根据已选 carriers/locations 返回需禁用的目的地
   *
   * 自带 cancelToken 是为了跳过请求层的「同 URL 重复请求自动取消」：
   * 该机制的 pending key 实际只用到 method + url + params（CancelTypesEnum.ALL === 0 是假值，
   * 判定分支永不成立，请求体没参与 key），所以同一时刻发往本接口的多个请求会互相取消；
   * 而被取消的请求在响应拦截器里返回的是永不 settle 的 promise，调用方会被永久挂住。
   * 本接口要按维度上下文并发发多次（见规则弹窗注释），必须opt out。
   * addPending 里是 `config.cancelToken = config.cancelToken || ...`，自带即不会被登记进 pending。
   */
  checkEventRuleAvailability(data: EventRuleAvailabilityPayload) {
    return baseRequest.post<EventRuleAvailabilityResponse>("v2/integration/event-rules/availability", data, {
      cancelToken: new axios.CancelToken(() => undefined),
    })
  },
}
